import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { authApi, needsSecondFactor } from '@/api/endpoints'
import { ApiError } from '@/api/client'
import { keys } from '@/api/keys'
import type { User } from '@/api/types'

/** 应用启动时请求一次 /auth/me，之后整个应用共享这份登录态。 */
export function useCurrentUser() {
  const query = useQuery<User | null>({
    queryKey: keys.me(),
    queryFn: async () => {
      try {
        return await authApi.me()
      } catch (error) {
        // 未登录是正常状态，不当作错误
        if (error instanceof ApiError && error.isAuthError) return null
        throw error
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    isAuthenticated: Boolean(query.data),
    error: query.error,
  }
}

export function useLogin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: authApi.login,
    onSuccess: (result) => {
      // 开了两步验证的只拿到 challenge，还没登录（D89）
      if (needsSecondFactor(result)) return
      client.setQueryData(keys.me(), result.user)
      client.invalidateQueries({ queryKey: keys.timelineRoot() })
    },
  })
}

export function useLoginTwoFactor() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: authApi.loginTwoFactor,
    onSuccess: ({ user }) => {
      client.setQueryData(keys.me(), user)
      client.invalidateQueries({ queryKey: keys.timelineRoot() })
    },
  })
}

export function useRegister() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: authApi.register,
    onSuccess: (user) => {
      client.setQueryData(keys.me(), user)
    },
  })
}

export function useLogout() {
  const client = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      client.setQueryData(keys.me(), null)
      client.clear()
      navigate('/login', { replace: true })
    },
  })
}
